import React, { FC, useEffect, useState } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { Selector } from 'common/components/Selector/Selector';
import { InfoTooltip } from 'common/components/InfoTooltip';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesHeatmapModel } from 'app/store/reducers/monitorSales.reducer';
import {
  EMonitorSalesSalesHeatmapMetric,
  IMonitorSalesHeatmapCell,
  IMonitorSalesParams,
} from 'entities/MonitorSales/MonitorSales.models';

const METRIC_OPTIONS = [
  { value: EMonitorSalesSalesHeatmapMetric.TC, label: 'TC' },
  { value: EMonitorSalesSalesHeatmapMetric.Sales, label: 'Sales' },
  { value: EMonitorSalesSalesHeatmapMetric.AC, label: 'AC' },
];

// Second filter: category selector — source TODO
const CATEGORY_OPTIONS = [{ value: 'Sides', label: 'Sides' }];

type LevelClass = 0 | 1 | 2;

const LEVEL_MAP: Record<string, LevelClass> = {
  high: 2,
  medium: 1,
  low: 0,
};

function buildGrid(cells: IMonitorSalesHeatmapCell[], hourCount: number, dayCount: number): LevelClass[][] {
  const grid: LevelClass[][] = Array.from({ length: hourCount }, () => Array(dayCount).fill(0) as LevelClass[]);
  for (const cell of cells) {
    if (cell.hour < hourCount && cell.day < dayCount) {
      grid[cell.hour][cell.day] = (LEVEL_MAP[cell.level] ?? 0) as LevelClass;
    }
  }
  return grid;
}

// Parse the start hour from a string like "6-7" → 6, "22-23" → 22
function startHour(label: string): number {
  return parseInt(label.split('-')[0], 10);
}

interface IShift {
  label: string;
  hourIndices: number[];
}

// Group hour indices into shifts: Shift 1 = 6–13, Shift 2 = 14–21, Shift 3 = 22+/0–5
function buildShifts(hours: string[]): IShift[] {
  const s1: number[] = [];
  const s2: number[] = [];
  const s3: number[] = [];

  hours.forEach((h, i) => {
    const sh = startHour(h);
    if (sh >= 6 && sh < 14) {
      s1.push(i);
    } else if (sh >= 14 && sh < 22) {
      s2.push(i);
    } else {
      s3.push(i);
    }
  });

  const shifts: IShift[] = [];
  if (s1.length) {
    shifts.push({ label: 'Shift 1', hourIndices: s1 });
  }
  if (s2.length) {
    shifts.push({ label: 'Shift 2', hourIndices: s2 });
  }
  if (s3.length) {
    shifts.push({ label: 'Shift 3', hourIndices: s3 });
  }
  return shifts;
}

interface IComponentProps {
  params?: IMonitorSalesParams;
}

export const SalesHeatmapBlock: FC<IComponentProps> = ({ params }) => {
  const dispatch = useAppDispatch();
  const [metric, setMetric] = useState<EMonitorSalesSalesHeatmapMetric>(EMonitorSalesSalesHeatmapMetric.TC);

  const { monitorSalesHeatmapModel, monitorSalesHeatmapModelLoading } = useAppSelector((state) => state.monitorSales);

  useEffect(() => {
    dispatch(getMonitorSalesHeatmapModel({ ...params, metric }));
  }, [params?.storeCode, params?.week, metric]);

  const { hours = [], days = [], cells = [] } = monitorSalesHeatmapModel ?? {};
  const grid = buildGrid(cells, hours.length, days.length);
  const shifts = buildShifts(hours);

  const handleMetricChange = (metricValue?: string | number): void => {
    if (metricValue) {
      setMetric(metricValue as EMonitorSalesSalesHeatmapMetric);
    }
  };

  return (
    <div className="ms-block ms-block--heatmap">
      <div className="ms__header">
        <span className="ff-medium fs-xs">The darker, the busier it gets!</span>
        <InfoTooltip
          title={
            <div>
              <div className="mb-3 ff-medium fs-xxs">The darker, the busier it gets!</div>
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">See each day’s hourly activity at a glance!</div>
              <Row className="mb-4">
                <Col span={24} className="ff-regular fs-xxxs" style={{ color: '#3359ff' }}>
                  Target exceeded
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={24} className="ff-regular fs-xxxs" style={{ color: '#a8b8ff' }}>
                  Target hit
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={24} className="ff-regular fs-xxxs" style={{ color: '#e0e3eb' }}>
                  Target missed
                </Col>
              </Row>
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">Click the dropdown to switch between:</div>
              <Row className="mb-4">
                <Col span={24} className="ff-regular fs-xxxs">
                  Sales / TC (Transaction Count) / AC (Average Check)
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={24} className="ff-regular fs-xxxs">
                  Product Categories
                </Col>
              </Row>
            </div>
          }
        />
      </div>

      <div className="ms__body">
        <div className="ms-heatmap__filters">
          <div>
            <Selector allowClear={false} value={metric} options={METRIC_OPTIONS} onChange={handleMetricChange} />
          </div>
          <div>
            {/* TODO add categories list */}
            <Selector allowClear={false} value="Sides" options={CATEGORY_OPTIONS} />
          </div>
        </div>

        {monitorSalesHeatmapModelLoading ? (
          <Skeleton active paragraph={{ rows: 10 }} title={false} />
        ) : (
          <>
            {shifts.map((shift) => (
              <React.Fragment key={shift.label}>
                <div className="ms-heatmap__shift-label">{shift.label}</div>
                <div className="ms-heatmap__days-row">
                  {days.map((day, i) => (
                    <div key={i} className="ms-heatmap__day-label">
                      {day}
                    </div>
                  ))}
                </div>
                {shift.hourIndices.map((hourIdx) => (
                  <div key={hourIdx} className="ms-heatmap__hour-row">
                    <div className="ms-heatmap__hour-label">{hours[hourIdx]}</div>
                    {days.map((_, dayIdx) => (
                      <div key={dayIdx} className={`ms-heatmap__cell ms-heatmap__cell--${grid[hourIdx][dayIdx]}`} />
                    ))}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
